import React, { FC, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { trackClarityEvent } from '../../../app/utlis/ClarityAnalytics'
import { trackFirebaseEvent } from '../../../app/utlis/FirebaseAnalytics'
import { CLARITY_EVENTS } from '../../../app/utlis/EventTypes'

import { getPageData, useData } from '../../../app/helper/functions'

import CALL_IMAGE from '../../../app/assets/images/homepage/faqcall.svg'
import FAQ_IMAGE from '../../../app/assets/images/homepage/faq_image.webp'
import CTA_BG from '../../../app/assets/images/homepage/earth.webp'

import Heading from '../../atoms/Heading/Heading'

interface AppsFaqProps {
  data?: {
    title?: string
    subTitle?: string
  }
}

const AppsFaq: FC<AppsFaqProps> = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0)
  const navigate = useNavigate()

  const { data: contextData } = useData()

  const homePageData = getPageData(contextData, 'HomePageData')

  // STATIC HEADING DATA
  const faqTitle = 'FAQ'

  const faqSubTitle =
    'Frequently asked questions (FAQ)'

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  const handleStartScopingConversation = () => {
    const payload = { source: 'apps_faq' }
    trackClarityEvent(CLARITY_EVENTS.START_SCOPING_CONVERSATION_CLICKED, payload)
    trackFirebaseEvent(CLARITY_EVENTS.START_SCOPING_CONVERSATION_CLICKED, payload)
    navigate('/book')
  }

  return (
    <>
      {/* FAQ SECTION */}
      <section
        className="relative overflow-hidden px-4 pb-14 pt-14 md:pb-20 md:pt-20"
        style={{
          backgroundColor: '#0F1117',
          backgroundImage:
            'radial-gradient(ellipse 66% 42% at 50% 54%, rgba(34,156,255,0.12) 0%, rgba(34,156,255,0.04) 34%, transparent 62%), linear-gradient(180deg, #0F1117 0%, #10141B 100%)',
        }}
      >
        <div className="mx-auto max-w-[1320px]">
          <Heading
            title={faqTitle}
            subTitle={faqSubTitle}
            dotsPosition="afterSubtitle"
            className="!px-0"
          />

          <div className="mt-10 grid grid-cols-1 items-stretch gap-8 md:mt-14 md:gap-10 lg:grid-cols-[420px_minmax(0,1fr)]">
            {/* LEFT IMAGE */}
              <div
  className="relative flex min-h-[250px] items-center justify-center overflow-hidden rounded-[30px] p-5 md:min-h-[398px] md:p-6"
  style={{
    border: '1px solid rgba(34, 156, 255, 0.15)',
  }}
>
                {/* GLOW */}
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      'radial-gradient(ellipse 48% 44% at 50% 56%, rgba(34, 156, 255, 0.28) 0%, rgba(34, 156, 255, 0.08) 42%, transparent 72%)',
                  }}
                />

                <img
                  src={FAQ_IMAGE}
                  alt="FAQ illustration"
                  className="relative z-10 h-auto w-full max-w-[200px] object-contain md:max-w-[290px]"
                />
              </div>

            {/* FAQ LIST */}
            <div className="relative px-5 lg:px-0 py-3 md:py-4">
              {/* CENTER GLOW */}
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    'radial-gradient(ellipse 56% 42% at 50% 50%, rgba(34, 156, 255, 0.14) 0%, transparent 72%)',
                }}
              />

              {homePageData.faqData.map((item: any, index: number) => {
                const isOpen = openIndex === index

                return (
                  <div
                    key={index}
                    className="group relative z-10 border-b last:border-b-0"
                    style={{
                      borderColor: 'rgba(34, 156, 255, 0.18)',
                    }}
                  >
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="flex w-full items-start justify-between gap-5 py-6 text-left md:py-7"
                    >
                      <span className="max-w-[88%] pr-3 font-google-sans-medium text-[16px] leading-[1.28] text-white md:text-[20px]">
                        {item.question}
                      </span>

                      <span
                        className={`mt-0.5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full transition-colors md:h-12 md:w-12 ${
                          isOpen ? 'bg-[#2A8BFD]' : 'bg-[#242D3D]'
                        }`}
                      >
                        <svg
                          className={`h-[16px] w-[16px] transition-transform md:h-[20px] md:w-[20px] ${
                            isOpen
                              ? 'rotate-90 text-white'
                              : 'text-[#2A8BFD]'
                          }`}
                          viewBox="0 0 12 12"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M4 2.2L9 6L4 9.8V2.2Z" />
                        </svg>
                      </span>
                    </button>

                    <p
                      className={`overflow-hidden pr-2 font-google-sans-regular text-[14px] leading-[1.7] text-[#BFC7D4] transition-all duration-500 md:pr-12 md:text-[16px] ${
                        isOpen
                          ? 'max-h-[400px] pb-5 opacity-100'
                          : 'max-h-0 pb-0 opacity-0'
                      }`}
                    >
                      {item.answer}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="relative overflow-hidden bg-[#0F1117] px-4 ">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="relative mx-auto flex min-h-[400px]  items-center overflow-hidden px-2 py-14 md:px-14 md:py-16"
        >
          {/* LEFT BLUE GLOW */}
          <div className="pointer-events-none absolute left-[10%] top-[45%] h-[320px] w-[320px] -translate-y-1/2 rounded-full bg-[#229CFF]/25 blur-[170px]" />

          {/* RIGHT IMAGE + SVG */}
          <div className="pointer-events-none absolute right-0 top-0 h-full w-[38%] overflow-hidden">
            {/* BG IMAGE */}
            <img
              src={CTA_BG}
              alt="CTA Background"
              className="h-full w-full object-cover opacity-100"
            />

            {/* SVG ICON IMAGE */}
           <img
  src={CALL_IMAGE}
  alt="Icon"
  className="hidden md:block absolute md:right-[17%] md:top-[70%] md:w-[100px] lg:right-[17%] lg:top-[80%] xl:right-[17%] xl:top-[60%] z-10 lg:w-[150px] xl:w-[180px] -translate-y-1/2 object-contain opacity-90"
/>
          </div>

          {/* CONTENT */}
          <div className="relative z-20 flex w-full flex-col items-center justify-center text-center">
            <div className="max-w-[1330px]">
              <h2 className="font-google-sans-medium text-[25px] leading-[1.1] text-white md:text-[35px] xl:text-[40px]">
                Bring us a problem. We'll tell you exactly how we'd solve it.
              </h2>

              <p className="mx-auto mt-6 max-w-[900px] font-google-sans-regular text-[16px] leading-[1.9] text-white/65 md:text-[18px]">
                If you're dealing with a process that's breaking under scale, a
                product that needs to ship, or a workflow that costs your team
                hours every week — book a 30-minute scoping call. No pitch
                decks, no NDAs on the first conversation. Just a direct
                technical discussion.
              </p>

              {/* BUTTON */}
              <button
                onClick={handleStartScopingConversation}
                className="mt-9 inline-flex items-center gap-2 rounded-[10px] bg-[#229CFF] px-8 py-4 font-google-sans-medium text-[16px] text-white transition-all duration-300 hover:scale-[1.03]"
                style={{
                  boxShadow: '0px 10px 13px 0px #229CFF4D',
                }}
              >
                Book Your Free 30-Min Review →
              </button>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  )
}

export default AppsFaq